import React, { useEffect } from 'react';
import styled from 'styled-components';
import Functions from '@client/style/Functions';
import { Breakpoints, presetColors } from '@client/style/Variables';
import { TextStyle } from '@client/style/TextStyles';
import useIsMobile from '@client/core/CustomHooks/useIsMobile';
import CollectionInfoStore, { ICollectionInfo } from './ContentTypeStore';
import { commonTextStyle } from '@client/style/Text';

import DraggableCarousel from '@client/core/modules/Carousel/DraggableCarousel';
import { ProgramTreetopHeaderImage } from './ProgramPageHeaderImage';
import { findContrastingColor } from '@client/core/utils/colors';
import WaveDivider from '../Divider/WaveDivider';
import PageHandler, { HistoryState } from '@client/core/PageSwitch/PageHandler';

const ListPageNav = styled.div<{ $color?: string }>`
	width: 100%;
	margin: 0px var(--gridMargin);
	--color: ${props => props.$color};
	--text-color: ${props => {
		if (!props.$color) return presetColors.white;
		return findContrastingColor(props.$color, presetColors.white, presetColors.blackish);
	}};
	margin-bottom: var(--breaker-total);
`;
const StyledSubtitle = styled.p`
	${commonTextStyle}
	margin: 0px var(--gridMargin);
	padding-top: 20px;
	color: var(--text-color);
	text-align: left;
	background-color: var(--color);

	${Functions.breakpoint(Breakpoints.mobile)} {
		${TextStyle.size('p4')}
		text-align: center;
		margin: auto;
		width: ${Functions.col(12, 16)};
	}
`;

const StyledDraggableCarousel = styled(DraggableCarousel)`
	width: 100%;
	z-index: 1;
	height: 80vw;
	${Functions.breakpoint(Breakpoints.mobile)} {
		height: 400px;
	}
`;
const StyledDesktopHeader = styled.div`
	width: 100%;
	z-index: 1;
	display: flex;
	align-items: flex-end;
	align-content: flex-end;
	justify-content: center;
	flex-wrap: wrap;
	margin: auto;
	/* gap: var(--gridGutter); */
	column-gap: var(--gridGutter);
	padding: 0 var(--gridGutter);
	& > * {
		cursor: pointer;
	}
	padding-top: 300px;
`;

const StyledCategoryButton = styled.div`
	display: block;
	appearance: none;
	text-align: left;
	z-index: 1;
	margin: 0;
	${TextStyle.size('p1', 'h4')}
	${TextStyle.family('title')}
	${TextStyle.weight('bold')}
	color: var(--text-color);
	white-space: nowrap;
	transition: opacity 0.2s ease;
	background: transparent;

	&:last-child {
		min-width: 100vw;
	}
	&:first-child {
		margin-left: var(--gridMargin);
		${Functions.breakpoint(Breakpoints.tablet)} {
			margin-left: 0;
		}
	}

	&:not(.active) {
		opacity: 0.1;
	}
`;

const StyledScrollContainer = styled.div`
	margin: 0;
	position: relative;
	z-index: 1;
	background-color: var(--color);
	${Functions.breakpoint(Breakpoints.tablet)} {
	}
`;

const StyledHeaderContainer = styled.div`
	position: absolute;
	left: 0;
	bottom: 0;
	right: 0;
	z-index: 0;
	pointer-events: none;
	top: -20px;
	${Functions.breakpoint(Breakpoints.mobile)} {
		top: -10vw;
	}
	${Functions.breakpoint(Breakpoints.desktop)} {
		top: -250px;
	}
`;

export default function CollectionPageSelector(props: { subtitle?: string }) {
	const activeCategory = CollectionInfoStore.use(state => state.activeInfo);
	const categories = CollectionInfoStore.use(state => state.collectionInfo);
	const isMobile = useIsMobile();

	const setActiveCategory = (item: ICollectionInfo) => {
		CollectionInfoStore.setSelectedCollectionInfo(item);
		if (item === undefined || item === null) return;
		PageHandler.getInstance().goto(
			window.location.pathname + '#' + item?.name?.toLowerCase().replaceAll(' ', ''),
			HistoryState.REPLACE
		);
		// window.history.replaceState(window.history.state || {}, '', '#' + item?.name?.toLowerCase().replaceAll(' ', ''));
	};

	useEffect(() => {
		// When categories change, set active category to be the first in the list
		const url: URL | undefined = typeof window !== 'undefined' ? new URL(window.location.href) : undefined;

		if (url !== undefined && url.hash) {
			const hash = url.hash.replace('#', '');
			const category = categories.find(({ name }) => name?.toLowerCase().replaceAll(' ', '') === hash);
			if (category) {
				setActiveCategory(category);
			}
		} else {
			setActiveCategory(categories[0]);
		}
	}, [categories]);

	const setActiveIndex = (i: number) => {
		setActiveCategory(categories[i]);
	};

	const activeIndex = activeCategory ? categories.findIndex(({ id }) => id === activeCategory.id) : 0;

	return (
		<ListPageNav $color={activeCategory?.headerColor?.color}>
			{!!categories.length && (
				<StyledScrollContainer>
					{isMobile && (
						<StyledDraggableCarousel
							className='dayCarousel'
							activeIndex={activeIndex}
							dragToNewIndex={i => {
								setActiveIndex(i);
							}}
						>
							{categories.map((category, i) => (
								<StyledCategoryButton className={i === activeIndex ? 'active' : ''} key={'catergory_' + i}>
									{category.name}
								</StyledCategoryButton>
							))}
							<StyledHeaderContainer data-carousel-ignore>
								<ProgramTreetopHeaderImage />
							</StyledHeaderContainer>
						</StyledDraggableCarousel>
					)}
					{!isMobile && (
						<StyledDesktopHeader>
							{categories.map((category, i) => (
								<StyledCategoryButton
									onClick={() => setActiveCategory(categories[i])}
									className={i === activeIndex ? 'active' : ''}
									key={'catergory_' + i}
								>
									{category.name}
								</StyledCategoryButton>
							))}
							<StyledHeaderContainer data-carousel-ignore>
								<ProgramTreetopHeaderImage />
							</StyledHeaderContainer>
						</StyledDesktopHeader>
					)}
					<StyledSubtitle>{props.subtitle}</StyledSubtitle>
				</StyledScrollContainer>
			)}

			<WaveDivider flip={true} fg={activeCategory?.headerColor?.color} index={activeIndex} />
		</ListPageNav>
	);
}
